<template>
  <div class="contact">
    <div class="container">
      <div class="contact-content">
        <h1 class="page-title">CONTACT US</h1>
        <div class="celtic-divider">
          <CelticBorder />
        </div>
        
        <section class="contact-section">
          <h2>Get in Touch</h2>
          <p>Have a question or want to get in touch with us? Fill out the form below and we'll get back to you as soon as possible.</p>
          
          <div v-if="formStatus.success" class="success-message">
            <h3>Thank you for your message!</h3>
            <p>We've received your inquiry and will get back to you as soon as possible.</p>
            <button @click="resetForm" class="reset-btn">Send Another Message</button>
          </div>
          
          <div v-else class="contact-form">
            <form @submit.prevent="sendEmail">
              <div class="form-group">
                <label for="name">Name</label>
                <input 
                  type="text" 
                  id="name" 
                  v-model="formData.name" 
                  required
                  placeholder="Your name"
                  :disabled="formStatus.loading"
                />
              </div>
              
              <div class="form-group">
                <label for="email">Email</label>
                <input 
                  type="email" 
                  id="email" 
                  v-model="formData.email" 
                  required
                  placeholder="Your email address"
                  :disabled="formStatus.loading"
                />
              </div>
              
              <div class="form-group">
                <label for="subject">Subject</label>
                <input 
                  type="text" 
                  id="subject" 
                  v-model="formData.subject" 
                  required
                  placeholder="Subject of your message"
                  :disabled="formStatus.loading"
                />
              </div>
              
              <div class="form-group">
                <label for="message">Message</label>
                <textarea 
                  id="message" 
                  v-model="formData.message" 
                  required
                  placeholder="Your message"
                  rows="5"
                  :disabled="formStatus.loading"
                ></textarea>
              </div>
              
              <div v-if="formStatus.error" class="error-message">
                <p>{{ formStatus.errorMessage }}</p>
                <p v-if="formStatus.errorMessage === 'internal'">
                  Please try again later or contact us directly at 
                  <a href="mailto:thehollowaynorwich@gmail.com">thehollowaynorwich@gmail.com</a>
                </p>
                <button @click="sendEmail()" class="retry-btn" :disabled="formStatus.loading">
                  Try Again
                </button>
              </div>
              
              <button 
                type="submit" 
                class="submit-btn" 
                :disabled="formStatus.loading"
              >
                <span v-if="formStatus.loading">Sending...</span>
                <span v-else>Send Message</span>
              </button>
            </form>
          </div>
          
          <div class="contact-info">
            <h3>Other Ways to Reach Us</h3>
            <p>Email: <a href="mailto:thehollowaynorwich@gmail.com">thehollowaynorwich@gmail.com</a></p>
            <p>Visit us at The Holloway, Norwich</p>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { functions } from '@/services/firebase'
import { httpsCallable } from 'firebase/functions'
import CelticBorder from '@/components/CelticBorder.vue'

const formData = ref({
  name: '',
  email: '',
  subject: '',
  message: ''
})

const formStatus = ref({
  loading: false,
  success: false,
  error: false,
  errorMessage: ''
})

const resetForm = () => {
  formData.value = {
    name: '',
    email: '',
    subject: '',
    message: ''
  }
  formStatus.value = {
    loading: false,
    success: false,
    error: false,
    errorMessage: ''
  }
}

const sendEmail = async (retryCount = 0) => {
  try {
    formStatus.value.loading = true
    formStatus.value.error = false
    
    const sendContactEmailFn = httpsCallable(functions, 'sendContactEmail')
    const result = await sendContactEmailFn(formData.value)
    console.log('Function result:', result)
    
    formStatus.value.success = true
  } catch (error) {
    console.error('Error sending email:', error)
    // Log more detailed error information
    if (error.code) console.error('Error code:', error.code)
    if (error.details) console.error('Error details:', error.details)
    if (error.message) console.error('Error message:', error.message)
    if (error.stack) console.error('Error stack:', error.stack)
    
    // Retry up to 2 times for internal errors
    if (error.message === 'internal' && retryCount < 2) {
      console.log(`Retrying... Attempt ${retryCount + 1}`)
      setTimeout(() => {
        sendEmail(retryCount + 1)
      }, 1000) // Wait 1 second before retrying
      return
    }
    
    formStatus.value.error = true
    formStatus.value.errorMessage = error.message || 'Failed to send your message. Please try again later.'
    
    // If the error is "internal", provide a more helpful message
    if (error.message === 'internal') {
      formStatus.value.errorMessage = 'An internal server error occurred. Please try again later or contact us directly at thehollowaynorwich@gmail.com'
    }
  } finally {
    formStatus.value.loading = false
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/main.scss';

.contact {
  width: 100%;
  overflow-x: hidden;
  background-color: rgb(242, 253, 247);
  padding-top: 2rem;
  padding-bottom: 4rem;
  position: relative;
  z-index: 1;
  
  .container {
    width: 100%;
    max-width: 100%;
    padding: 0 $spacing-sm;
    box-sizing: border-box;
    margin: 0 auto;
    position: relative;
    z-index: 2;
    
    @media (min-width: $breakpoint-md) {
      max-width: 1200px;
      padding: 0 $spacing-md;
    }
  }
  
  .page-title {
    font-family: $font-family-headers;
    color: $tertiary-color;
    text-align: center;
    margin-bottom: 1rem;
    font-size: 2.5rem;
  }
  
  .celtic-divider {
    width: 100%;
    max-width: 300px;
    margin: 0 auto 2rem;
  }
  
  .contact-section {
    background-color: white;
    border-radius: 8px;
    padding: 2rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.08);
    margin-bottom: 2rem;
    
    h2 {
      color: $tertiary-color;
      font-family: $font-family-headers;
      margin-bottom: 1rem;
    }
    
    p {
      margin-bottom: 1.5rem;
      line-height: 1.6;
    }
  }
  
  .success-message {
    background-color: rgba(0, 184, 148, 0.1);
    padding: 2rem;
    border-radius: 8px;
    text-align: center;
    
    h3 {
      color: $tertiary-color;
      font-family: $font-family-headers;
      margin-bottom: 1rem;
    }
    
    .reset-btn {
      background-color: $tertiary-color;
      color: white;
      border: none;
      padding: 0.75rem 1.5rem;
      font-size: 1rem;
      border-radius: 4px;
      cursor: pointer;
      margin-top: 1.5rem;
      transition: background-color 0.3s ease;
      
      &:hover {
        background-color: darken($tertiary-color, 10%);
      }
    }
  }
  
  .error-message {
    background-color: rgba(255, 99, 71, 0.1);
    padding: 1rem;
    border-radius: 4px;
    margin-bottom: 1.5rem;
    
    p {
      color: #e74c3c;
      margin: 0;
      margin-bottom: 0.5rem;
    }
    
    a {
      color: #e74c3c;
      text-decoration: underline;
    }
    
    .retry-btn {
      background-color: #e74c3c;
      color: white;
      border: none;
      padding: 0.5rem 1rem;
      font-size: 0.9rem;
      border-radius: 4px;
      cursor: pointer;
      margin-top: 0.5rem;
      transition: background-color 0.3s ease;
      
      &:hover:not(:disabled) {
        background-color: darken(#e74c3c, 10%);
      }
      
      &:disabled {
        background-color: #f8d7da;
        cursor: not-allowed;
      }
    }
  }
  
  .contact-form {
    margin-bottom: 2rem;
    
    .form-group {
      margin-bottom: 1.5rem;
      
      label {
        display: block;
        margin-bottom: 0.5rem;
        font-weight: 500;
        color: $tertiary-color;
      }
      
      input, textarea {
        width: 100%;
        padding: 0.75rem;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-family: $font-family-main;
        font-size: 1rem;
        
        &:focus {
          outline: none;
          border-color: $tertiary-color;
          box-shadow: 0 0 0 2px rgba(0, 184, 148, 0.2);
        }
        
        &:disabled {
          background-color: #f8f9fa;
          cursor: not-allowed;
        }
      }
    }
    
    .submit-btn {
      background-color: $tertiary-color;
      color: white;
      border: none;
      padding: 0.75rem 1.5rem;
      font-size: 1rem;
      border-radius: 4px;
      cursor: pointer;
      transition: background-color 0.3s ease;
      min-width: 150px;
      
      &:hover:not(:disabled) {
        background-color: darken($tertiary-color, 10%);
      }
      
      &:disabled {
        background-color: #b2dfdb;
        cursor: not-allowed;
      }
    }
  }
  
  .contact-info {
    background-color: rgba(0, 184, 148, 0.05);
    padding: 1.5rem;
    border-radius: 4px;
    
    h3 {
      color: $tertiary-color;
      font-family: $font-family-headers;
      margin-bottom: 1rem;
    }
    
    p {
      margin-bottom: 0.5rem;
    }
    
    a {
      color: $tertiary-color;
      text-decoration: none;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
}
</style> 